import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterBar, { type FilterState } from "@/pages/category/components/FilterBar";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mediaList } from "@/mocks/media";
import { filterMedia, paginate, totalPages, type SortKey } from "@/utils/media";

const PAGE_SIZE = 8;

const config = {
  movie: {
    title: "电影频道",
    heading: "电影",
    desc: "汇集院线与独立电影佳作，涵盖科幻、犯罪、剧情、爱情等多种类型，助你快速找到想看的电影。",
  },
  tv: {
    title: "电视剧频道",
    heading: "电视剧",
    desc: "热播剧集与口碑好剧一站浏览，悬疑、都市、历史、青春等各类题材随心筛选。",
  },
};

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const safeType = type === "tv" ? "tv" : "movie";
  const meta = config[safeType];

  const filterState: FilterState = {
    region: searchParams.get("region") || "全部",
    year: searchParams.get("year") || "全部",
    genre: searchParams.get("genre") || "全部",
    sort: (searchParams.get("sort") as SortKey) || "latest",
  };
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  usePageMeta({
    title: `${meta.heading} - 聚会的目的在线`,
    description: `${meta.heading}频道：${meta.desc.replace(/。$/, "")}，支持地区、年份与类型筛选，快速发现优质影视资源。`,
    keywords: `聚会的目的在线,${meta.heading},影视资源,${meta.heading}推荐,在线影视`,
  });

  const filtered = useMemo(
    () =>
      filterMedia(mediaList, {
        type: safeType,
        region: filterState.region,
        year: filterState.year,
        genre: filterState.genre,
        sort: filterState.sort,
      }),
    [safeType, filterState.region, filterState.year, filterState.genre, filterState.sort]
  );

  const total = totalPages(filtered.length, PAGE_SIZE);
  const currentPage = Math.min(page, total);
  const pageItems = paginate(filtered, currentPage, PAGE_SIZE);

  const updateFilter = (next: Partial<FilterState>) => {
    const merged = { ...filterState, ...next };
    const params = new URLSearchParams();
    if (merged.region !== "全部") params.set("region", merged.region);
    if (merged.year !== "全部") params.set("year", merged.year);
    if (merged.genre !== "全部") params.set("genre", merged.genre);
    if (merged.sort !== "latest") params.set("sort", merged.sort);
    setSearchParams(params);
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams());
    setMobileFilterOpen(false);
  };

  return (
    <Layout>
      {/* Header */}
      <div className="w-full bg-primary-500 text-background-50">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-1.5 text-xs md:text-sm text-background-50/80 mb-4">
            <Link to="/" className="hover:text-white cursor-pointer transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{meta.heading}</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl">{meta.heading}</h1>
          <p className="mt-3 text-sm md:text-base text-background-50/85 max-w-2xl leading-relaxed">
            {meta.desc}
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar / mobile filter */}
          <aside className="w-full lg:w-64 shrink-0">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="lg:hidden w-full h-11 mb-4 px-4 rounded-md bg-background-100 border border-background-200 flex items-center justify-between text-sm text-foreground-800 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line text-primary-500"></i>
                筛选与排序
              </span>
              <i className={mobileFilterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>

            <div
              className={`${
                mobileFilterOpen ? "block" : "hidden"
              } lg:block lg:sticky lg:top-24 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5`}
            >
              <FilterBar
                value={filterState}
                onChange={updateFilter}
                onReset={resetFilters}
                resultCount={filtered.length}
              />
            </div>
          </aside>

          {/* Results */}
          <div className="flex-1 min-w-0">
            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                  {pageItems.map((item) => (
                    <MediaCard key={item.id} item={item} />
                  ))}
                </div>
                <Pagination current={currentPage} total={total} basePath={`/category/${safeType}`} />
              </>
            ) : (
              <EmptyState
                icon="ri-search-eye-line"
                title="没有找到符合条件的作品"
                description="试试调整地区、年份或类型筛选条件，或直接重置筛选查看全部内容。"
                actionLabel="重置筛选"
                onAction={resetFilters}
                actionTo="/"
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}